'use client';

import {
  RefreshApi,
  ResponseContext,
  type Configuration,
} from '@repo/api-gw-sdk';
import { createContext, useMemo, type PropsWithChildren } from 'react';

import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { createHttpClient, type HttpClient } from '@/data/dal/httpClient';
import { getParsedJwt } from '@/libs/auth';

import { createConfig } from './api-config';
import { useEnvironment } from './useEnvironment';

export const NonProjectDalContext = createContext<{
  nonProjectDal: ReturnType<typeof nonProjectDalFactory>;
  httpClient: HttpClient;
  configuration: Configuration;
} | null>(null);

export const NonProjectDalProvider = ({ children }: PropsWithChildren) => {
  const {
    clientApiBaseUrl: baseUrl,
    authToken,
    updateAuthToken,
  } = useEnvironment();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const httpClient = useMemo(() => createHttpClient(baseUrl, authToken), []);
  const configuration = useMemo(
    () =>
      createConfig(baseUrl, authToken, [
        {
          pre: async (context) => {
            if (!context.getUrl().endsWith('/refresh')) {
              const jwt = getParsedJwt(authToken);
              const exp = jwt?.exp;
              if (typeof exp === 'number') {
                const now = Math.floor(Date.now() / 1000);
                // if token expires in 2 minutes, refresh it
                if (exp < now + 120) {
                  const response = await new RefreshApi(
                    configuration
                  ).refresh();
                  if (response.message) {
                    updateAuthToken(response.message);
                  }

                  return context;
                }
              }
            }

            return context;
          },
          post: async (context) => {
            if (context.httpStatusCode === 401) {
              // The user is not authenticated, delete the next jwt and redirect to login
              location.href = '/logout';
            }
            if (context.httpStatusCode === 500) {
              const text = await context.body.text();
              window.reportError(text);
              return new ResponseContext(
                context.httpStatusCode,
                context.headers,
                {
                  text: () => Promise.resolve(text),
                  json: () => Promise.resolve(JSON.parse(text)),
                  binary: () =>
                    Promise.resolve(
                      new Blob([text], {
                        type: 'application/json',
                      })
                    ),
                }
              );
            }
            return context;
          },
        },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const nonProjectDal = nonProjectDalFactory(httpClient, configuration);

  return (
    <NonProjectDalContext.Provider
      value={{ nonProjectDal, httpClient, configuration }}
    >
      {children}
    </NonProjectDalContext.Provider>
  );
};
